import type { LogRecord, Sink } from '@logtape/logtape';

/**
 * Persistent ring buffer for auth logging (#3576).
 *
 * Console output is gone once the user reloads or signs in again, which is
 * exactly when a forced re-login gets reported. Every auth log record is
 * therefore also kept in localStorage so the sequence leading up to the login
 * screen can be read afterwards: `serv.authLog()` in the console, or the raw
 * `auth_log` key in DevTools → Application → Local Storage.
 */

export const AUTH_LOG_KEY = 'auth_log';
const MAX_ENTRIES = 400;

export interface AuthLogEntry {
  /** ISO timestamp */
  t: string;
  level: string;
  category: string;
  message: string;
  props?: Record<string, unknown>;
}

function renderMessage(record: LogRecord): string {
  return record.message
    .map((part) => (typeof part === 'string' ? part : safeStringify(part)))
    .join('');
}

function safeStringify(value: unknown): string {
  try {
    return typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Errors do not survive JSON.stringify; keep what matters. */
function serializable(value: unknown): unknown {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = serializable(v);
    }
    return out;
  }
  return value;
}

export function readAuthLog(): AuthLogEntry[] {
  try {
    const raw = localStorage.getItem(AUTH_LOG_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearAuthLog(): void {
  try {
    localStorage.removeItem(AUTH_LOG_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
}

/** Sink that appends every record to the localStorage ring buffer. */
export const authLogBufferSink: Sink = (record: LogRecord) => {
  try {
    const entries = readAuthLog();
    const props = record.properties as Record<string, unknown>;
    entries.push({
      t: new Date(record.timestamp).toISOString(),
      level: record.level,
      category: record.category.join('.'),
      message: renderMessage(record),
      ...(props && Object.keys(props).length > 0
        ? { props: serializable(props) as Record<string, unknown> }
        : {}),
    });
    if (entries.length > MAX_ENTRIES) {
      entries.splice(0, entries.length - MAX_ENTRIES);
    }
    localStorage.setItem(AUTH_LOG_KEY, JSON.stringify(entries));
  } catch {
    // quota / private mode — console sink still has the record
  }
};
